import { ApiError, NetworkError, parseProblem } from '@/lib/api/problem'
import { csrfHeader, readCsrfToken } from '@/lib/api/csrf'
import { apiBaseUrl } from '@/lib/env'

export type AuthBridge = {
  getAccessToken: () => string | null
  refreshSession: () => Promise<boolean>
  onSessionExpired: () => void
}

const detachedBridge: AuthBridge = {
  getAccessToken: () => null,
  refreshSession: () => Promise.resolve(false),
  onSessionExpired: () => undefined,
}

let bridge: AuthBridge = detachedBridge
let pendingRefresh: Promise<boolean> | null = null

export const setAuthBridge = (next: AuthBridge): void => {
  bridge = next
}

export const resetAuthBridge = (): void => {
  bridge = detachedBridge
  pendingRefresh = null
}

const refreshOnce = (): Promise<boolean> => {
  pendingRefresh ??= bridge.refreshSession().finally(() => {
    pendingRefresh = null
  })

  return pendingRefresh
}

export const correlationIdHeader = 'X-Correlation-Id'

export type QueryValue = string | number | boolean | undefined | null

export type RequestOptions = {
  method?: 'GET' | 'POST' | 'DELETE'
  query?: Record<string, QueryValue>
  json?: unknown
  formData?: FormData
  signal?: AbortSignal
  authenticated?: boolean
  csrf?: boolean
}

const buildUrl = (path: string, query: Record<string, QueryValue> | undefined): string => {
  const search = new URLSearchParams()

  for (const [name, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(name, String(value))
    }
  }

  const suffix = search.size === 0 ? '' : `?${search.toString()}`

  return `${apiBaseUrl}${path}${suffix}`
}

const toApiError = async (response: Response): Promise<ApiError> => {
  const correlationId = response.headers.get(correlationIdHeader)

  const problem = await response
    .json()
    .then((body: unknown) => parseProblem(body))
    .catch(() => null)

  return new ApiError(response.status, problem, correlationId)
}

const send = async (
  path: string,
  options: RequestOptions,
  allowRefresh: boolean,
): Promise<Response> => {
  const headers = new Headers({ Accept: 'application/json, application/problem+json' })

  if (options.json !== undefined) {
    headers.set('Content-Type', 'application/json')
  }

  if (options.csrf === true) {
    const csrfToken = readCsrfToken()

    if (csrfToken !== null) {
      headers.set(csrfHeader, csrfToken)
    }
  }

  const token = options.authenticated === false ? null : bridge.getAccessToken()

  if (token !== null) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  let response: Response

  try {
    response = await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      body: options.formData ?? (options.json === undefined ? null : JSON.stringify(options.json)),
      signal: options.signal ?? null,
      credentials: 'include',
    })
  } catch (cause) {
    throw new NetworkError(cause)
  }

  if (response.status === 401 && allowRefresh && options.authenticated !== false) {
    const refreshed = await refreshOnce()

    if (refreshed) {
      return send(path, options, false)
    }

    bridge.onSessionExpired()
  }

  return response
}

export const apiRequest = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
  const response = await send(path, options, true)

  if (!response.ok) {
    throw await toApiError(response)
  }

  const text = await response.text()

  if (text === '') {
    return undefined as T
  }

  const body: unknown = JSON.parse(text)

  return body as T
}

export const apiDownload = async (path: string, options: RequestOptions = {}): Promise<Blob> => {
  const response = await send(path, options, true)

  if (!response.ok) {
    throw await toApiError(response)
  }

  return response.blob()
}
